import { useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";

// Emplacement réservé — remplacer par de vraies photos avec l'accord du client.
const plats = [
  { src: "https://images.unsplash.com/photo-1541518763669-27fef04b14ea?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", alt: "Couscous tunisien" },
  { src: "https://images.unsplash.com/photo-1682370207954-c8a9cccaabb4?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", alt: "Tajine traditionnel" },
  { src: "https://images.unsplash.com/photo-1761828122856-8703baac8e86?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", alt: "Pâtisseries orientales" },
  { src: "https://images.unsplash.com/photo-1591299089616-c9604047b1a6?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", alt: "Thé à la menthe" },
  { src: "https://images.unsplash.com/photo-1590237581598-988d27565521?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", alt: "Épices méditerranéennes" },
  { src: "https://images.unsplash.com/photo-1651440204216-548382747b40?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", alt: "Ambiance du restaurant" },
];

// Emplacement réservé — remplacer par de vraies photos avec l'accord du client.
const evenements = [
  { src: "https://images.unsplash.com/photo-1555244162-803834f70033?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", alt: "Buffet de mariage" },
  { src: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", alt: "Table dressée pour un événement" },
  { src: "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", alt: "Réception en extérieur" },
  { src: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", alt: "Service à table lors d'une réception" },
  { src: "https://images.unsplash.com/photo-1551798507-629020c81463?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", alt: "Buffet d'événement d'entreprise" },
  { src: "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", alt: "Fête familiale" },
];

const GalleryGrid = ({ photos, section, onOpen }) => (
  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
    {photos.map((p, i) => (
      <Reveal key={p.src} delay={i * 0.05}>
        <button
          type="button"
          data-testid={`gallery-${section}-${i}`}
          onClick={() => onOpen(section, i)}
          className="block w-full rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow focus:outline-none focus:ring-2 focus:ring-terracotta"
          aria-label={`Agrandir : ${p.alt}`}
        >
          <img src={p.src} alt={p.alt} loading="lazy" width="900" height="600" className="h-48 md:h-64 w-full object-cover transition-transform duration-500 hover:scale-105" />
        </button>
      </Reveal>
    ))}
  </div>
);

export default function Galerie() {
  const [lightbox, setLightbox] = useState(null); // { section, index }
  const photos = lightbox ? (lightbox.section === "plats" ? plats : evenements) : [];

  const open = (section, index) => setLightbox({ section, index });
  const close = () => setLightbox(null);
  const nav = (dir) =>
    setLightbox((lb) => ({
      ...lb,
      index: (lb.index + dir + photos.length) % photos.length,
    }));

  return (
    <main id="contenu-principal">
      <Seo
        title="Galerie photos — Plats et événements | TN FOOD"
        description="En images : nos spécialités tunisiennes (couscous, tajines, pâtisseries orientales) et nos événements traiteur (buffets, tables dressées, mariages) à Troyes et dans l'Aube."
        path="/galerie"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ImageGallery",
          "name": "Galerie TN FOOD — Plats et événements",
        }}
      />

      <section className="bg-bordeaux text-cream py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <Reveal>
            <h1 className="font-display text-4xl md:text-5xl font-bold">Galerie</h1>
            <p className="mt-4 text-lg text-cream/85 max-w-2xl">Nos plats et nos événements en images.</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="galerie-plats">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <Reveal className="mb-8">
            <h2 className="font-display text-3xl font-semibold text-encre">Nos plats</h2>
          </Reveal>
          <GalleryGrid photos={plats} section="plats" onOpen={open} />
        </div>
      </section>

      <section className="bg-cream py-16 md:py-24" data-testid="galerie-evenements">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <Reveal className="mb-8">
            <h2 className="font-display text-3xl font-semibold text-encre">Nos événements</h2>
          </Reveal>
          <GalleryGrid photos={evenements} section="evenements" onOpen={open} />
        </div>
      </section>

      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Visionneuse photo"
          data-testid="lightbox"
          className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4"
          onClick={close}
        >
          <button type="button" data-testid="lightbox-close" aria-label="Fermer" onClick={close} className="absolute top-5 right-5 text-cream p-2">
            <X className="h-8 w-8" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            data-testid="lightbox-prev"
            aria-label="Photo précédente"
            onClick={(e) => { e.stopPropagation(); nav(-1); }}
            className="absolute left-4 md:left-8 text-cream p-2"
          >
            <ChevronLeft className="h-10 w-10" strokeWidth={1.5} />
          </button>
          <img
            src={photos[lightbox.index].src}
            alt={photos[lightbox.index].alt}
            className="max-h-[85vh] max-w-full rounded-xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            data-testid="lightbox-next"
            aria-label="Photo suivante"
            onClick={(e) => { e.stopPropagation(); nav(1); }}
            className="absolute right-4 md:right-8 text-cream p-2"
          >
            <ChevronRight className="h-10 w-10" strokeWidth={1.5} />
          </button>
        </div>
      )}
    </main>
  );
}
